import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import styles from './ContractorProfiles.module.css';

/**
 * ContractorProfiles component that displays comprehensive contractor information
 * including contact details, project scope, specifications, payment terms, and more
 * 
 * Props:
 * - consolidatedData: Full contractor analysis data
 * - evaluationData: Contractor evaluation scores
 * - userPlan: 'free', 'essential', 'complete', 'expert'
 * - hasActiveSubscription: boolean
 * - onUpgradeClick: function to handle upgrade clicks
 */
const ContractorProfiles = ({
  consolidatedData,
  evaluationData,
  userPlan = 'free',
  hasActiveSubscription = false,
  onUpgradeClick
}) => {
  const [expandedContractors, setExpandedContractors] = useState({});
  const [activeTab, setActiveTab] = useState({});

  // Plan-based feature access control
  const canAccessBasicProfiles = () => {
    // Essential+ plans get basic contractor profiles
    return hasActiveSubscription && ['essential', 'complete', 'expert'].includes(userPlan);
  };

  const canAccessDetailedProfiles = () => {
    // Complete+ plans get detailed contractor profiles (as per pricing page)
    return hasActiveSubscription && ['complete', 'expert'].includes(userPlan);
  };

  const canAccessAdvancedFeatures = () => {
    // Expert plan gets advanced profile features
    return hasActiveSubscription && userPlan === 'expert';
  };

  // Extract contractor data
  const contractors = consolidatedData ? 
    [1, 2, 3, 4, 5]
      .map(num => consolidatedData[`contractor_${num}`])
      .filter(Boolean) 
    : [];

  // Get contractor colors
  const getContractorColors = () => {
    const colors = ['#0077cc', '#ff9800', '#4caf50', '#9c27b0', '#f44336'];
    const contractorColors = {};
    contractors.forEach((contractor, index) => {
      const name = contractor.contractor.contractor_name;
      contractorColors[name] = colors[index % colors.length];
    });
    return contractorColors;
  };

  const contractorColors = getContractorColors();

  // Toggle contractor expansion
  const toggleContractor = (contractorName) => {
    setExpandedContractors(prev => ({
      ...prev,
      [contractorName]: !prev[contractorName]
    }));
  };

  // Set active tab for contractor
  const setContractorTab = (contractorName, tab) => {
    setActiveTab(prev => ({
      ...prev,
      [contractorName]: tab
    }));
  };

  // Parse cost safely
  const parseCost = (costValue) => {
    if (!costValue) return 0;
    const costStr = costValue.toString();
    const cleanCost = costStr.replace(/[\$,]/g, '');
    return parseFloat(cleanCost) || 0;
  };

  // Calculate total allowances
  const calculateTotalAllowances = (allowances) => {
    if (!allowances?.direct_costs) return 0;
    return Object.values(allowances.direct_costs).reduce((sum, val) => {
      const numVal = typeof val === 'string' ? parseFloat(val.replace(/[\$,]/g, '')) : val;
      return sum + (numVal || 0);
    }, 0);
  };

  // Get evaluation score for contractor
  const getEvaluationScore = (contractorName, contractorIndex) => {
    const evaluationKey = `contractor_${contractorIndex + 1}`;
    const contractorEval = evaluationData?.[evaluationKey];
    
    if (!contractorEval) return null;
    
    const categories = [
      'communication_and_accountability',
      'documentation_and_deliverables', 
      'logistics_and_execution_plan',
      'scope_and_methodology',
      'safety_measurements',
      'labor_and_workforce',
      'contracting_behavior'
    ];
    
    const validScores = categories
      .map(cat => contractorEval[cat]?.score)
      .filter(score => score !== undefined && score !== null);
    
    return validScores.length > 0 ? 
      Math.round(validScores.reduce((sum, score) => sum + score, 0) / validScores.length) : 0;
  };

  // Prepare allowances chart data
  const prepareAllowancesChart = (allowances) => {
    if (!allowances?.direct_costs) return [];
    
    return Object.entries(allowances.direct_costs)
      .map(([key, value]) => ({
        name: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
        value: typeof value === 'string' ? parseFloat(value.replace(/[\$,]/g, '')) : value || 0
      }))
      .filter(item => item.value > 0)
      .sort((a, b) => b.value - a.value)
      .slice(0, 6);
  };

  // Check if user has access to detailed contractor profiles
  if (!canAccessDetailedProfiles()) {
    return (
      <div className={styles.lockedContent}>
        <div className={styles.lockIcon}>
          <i className="fas fa-users"></i>
        </div>
        <h3>Detailed Contractor Profiles</h3>
        <p>Get in-depth information about each contractor including their expertise, project history, and detailed analysis to make the best decision for your project.</p>
        <div className={styles.featureList}>
          <ul>
            <li><i className="fas fa-check"></i> Comprehensive contractor background</li>
            <li><i className="fas fa-check"></i> Detailed project portfolio</li>
            <li><i className="fas fa-check"></i> Risk assessment & scoring</li>
            <li><i className="fas fa-check"></i> Payment structure analysis</li>
            <li><i className="fas fa-check"></i> Timeline & logistics breakdown</li>
          </ul>
        </div>
        <button className={styles.upgradeButton} onClick={onUpgradeClick}>
          <i className="fas fa-crown"></i> 
          {userPlan === 'free' 
            ? 'Upgrade to Complete Plan' 
            : userPlan === 'essential' 
            ? 'Upgrade to Complete Plan'
            : 'Upgrade to Access Profiles'
          }
        </button>
        <div className={styles.planNote}>
          <i className="fas fa-info-circle"></i>
          <span>Available with Complete and Expert plans</span>
        </div>
      </div>
    );
  }

  if (!contractors.length) {
    return (
      <div className={styles.emptyState}>
        <div className={styles.emptyIcon}>
          <i className="fas fa-users"></i>
        </div>
        <h3>No Contractor Data Available</h3>
        <p>Please upload contractor proposals to see detailed profiles.</p>
      </div>
    );
  }

  return (
    <div className={styles.profilesContainer}>
      <div className={styles.profilesHeader}>
        <h2>Contractor Profiles</h2>
        <p>Comprehensive analysis of {contractors.length} contractor{contractors.length > 1 ? 's' : ''} competing for your project</p>
        
        <div className={styles.profilesStats}>
          <div className={styles.statCard}>
            <div className={styles.statIcon}>
              <i className="fas fa-users"></i>
            </div>
            <div className={styles.statInfo}>
              <div className={styles.statValue}>{contractors.length}</div>
              <div className={styles.statLabel}>Contractors</div>
            </div>
          </div>
          
          <div className={styles.statCard}>
            <div className={styles.statIcon}>
              <i className="fas fa-dollar-sign"></i>
            </div>
            <div className={styles.statInfo}>
              <div className={styles.statValue}>
                ${(() => {
                  const costs = contractors.map(c => parseCost(c.payment_terms?.total_cost));
                  const range = Math.max(...costs) - Math.min(...costs);
                  return `${(range / 1000).toFixed(0)}k`;
                })()}
              </div>
              <div className={styles.statLabel}>Price Range</div>
            </div>
          </div>
          
          <div className={styles.statCard}>
            <div className={styles.statIcon}>
              <i className="fas fa-clock"></i>
            </div>
            <div className={styles.statInfo}>
              <div className={styles.statValue}>
                {(() => {
                  const durations = contractors
                    .map(c => c.timeline_details?.estimated_total_project_duration)
                    .filter(Boolean)
                    .map(d => parseInt(d.replace(/\D/g, '')))
                    .filter(n => !isNaN(n));
                  
                  if (durations.length === 0) return 'N/A';
                  const avg = Math.round(durations.reduce((sum, d) => sum + d, 0) / durations.length);
                  return `${avg}w`;
                })()}
              </div>
              <div className={styles.statLabel}>Avg Timeline</div>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.contractorsGrid}>
        {contractors.map((contractor, index) => {
                      const contractorName = contractor.contractor.contractor_name;
            const isExpanded = expandedContractors[contractorName];
            const currentTab = activeTab[contractorName] || 'overview';
          const contractorColor = contractorColors[contractorName];
          const totalCost = parseCost(contractor.payment_terms?.total_cost);
          const totalAllowances = calculateTotalAllowances(contractor.allowances);
          const evaluationScore = getEvaluationScore(contractorName, index);
          const allowancesData = prepareAllowancesChart(contractor.allowances);

          return (
            <div 
              key={index} 
              className={`${styles.contractorCard} ${isExpanded ? styles.expanded : ''}`}
            >
              {/* Contractor Header */}
              <div 
                className={styles.contractorHeader} 
                style={{ borderTopColor: contractorColor }}
                onClick={() => toggleContractor(contractorName)}
              >
                <div className={styles.contractorBasicInfo}>
                  <div 
                    className={styles.contractorAvatar}
                    style={{ backgroundColor: contractorColor }}
                  >
                    {contractorName.split(' ').map(word => word[0]).join('').toUpperCase().slice(0, 3)}
                  </div>
                  
                  <div className={styles.contractorDetails}>
                    <h3 className={styles.contractorName}>{contractorName}</h3>
                    <div className={styles.contractorContact}>
                      {contractor.contractor.contractor_phone && (
                        <span className={styles.contactItem}>
                          <i className="fas fa-phone"></i>
                          {contractor.contractor.contractor_phone}
                        </span>
                      )}
                      {contractor.contractor.contractor_email && (
                        <span className={styles.contactItem}>
                          <i className="fas fa-envelope"></i>
                          {contractor.contractor.contractor_email}
                        </span>
                      )}
                    </div>
                    {contractor.contractor.contractor_address && (
                      <div className={styles.contractorAddress}>
                        <i className="fas fa-map-marker-alt"></i>
                        {contractor.contractor.contractor_address}
                      </div>
                    )}
                  </div>
                </div>

                <div className={styles.contractorMetrics}>
                  <div className={styles.metric}>
                    <div className={styles.metricValue}>${totalCost.toLocaleString()}</div>
                    <div className={styles.metricLabel}>Total Cost</div>
                  </div>
                  
                  {evaluationScore !== null && (
                    <div className={styles.metric}>
                      <div className={`${styles.metricValue} ${styles.scoreValue}`}>
                        {evaluationScore}/10
                      </div>
                      <div className={styles.metricLabel}>Quality Score</div>
                    </div>
                  )}
                  
                  <div className={styles.metric}>
                    <div className={styles.metricValue}>
                      {contractor.timeline_details?.estimated_total_project_duration || 'TBD'}
                    </div>
                    <div className={styles.metricLabel}>Timeline</div>
                  </div>
                </div>

                <div 
                  className={styles.expandButton}
                  aria-label={isExpanded ? 'Collapse' : 'Expand'}
                >
                  <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'}`}></i>
                </div>
              </div>

              {/* Quick Overview */}
              <div className={styles.quickOverview}>
                <div className={styles.overviewItem}>
                  <span className={styles.overviewLabel}>License:</span>
                  <span className={styles.overviewValue}>
                    {contractor.license_details?.license_number || 'Not specified'}
                  </span>
                </div>
                
                <div className={styles.overviewItem}>
                  <span className={styles.overviewLabel}>Insurance:</span>
                  <span className={styles.overviewValue}>
                    ${contractor.insurance_details?.liability?.coverage?.replace(/[\$,]/g, '') ? 
                      `${(parseFloat(contractor.insurance_details.liability.coverage.replace(/[\$,]/g, '')) / 1000000).toFixed(1)}M` : 
                      'Not specified'}
                  </span>
                </div>
                
                <div className={styles.overviewItem}>
                  <span className={styles.overviewLabel}>Warranty:</span>
                  <span className={styles.overviewValue}>
                    {contractor.warranty_details?.workmanship?.warranty_duration || 'Not specified'}
                  </span>
                </div>
                
                <div className={styles.overviewItem}>
                  <span className={styles.overviewLabel}>Payment:</span>
                  <span className={styles.overviewValue}>
                    {contractor.payment_terms?.pricing_structure?.replace(/_/g, ' ') || 'Not specified'}
                  </span>
                </div>
              </div>

              {/* Expanded Content */}
              {isExpanded && (
                <>
                  {canAccessBasicProfiles() ? (
                    <div className={styles.expandedContent}>
                      {/* Tab Navigation */}
                      <div className={styles.tabNavigation}>
                        {[
                          { key: 'overview', label: 'Overview', icon: 'fas fa-chart-line' },
                          { key: 'scope', label: 'Scope & Specs', icon: 'fas fa-tasks' },
                          { key: 'financial', label: 'Financial', icon: 'fas fa-dollar-sign' },
                          { key: 'legal', label: 'Legal & Insurance', icon: 'fas fa-shield-alt' },
                          { key: 'logistics', label: 'Logistics', icon: 'fas fa-cogs' }
                        ].map(tab => (
                          <button
                            key={tab.key}
                            className={`${styles.tabButton} ${currentTab === tab.key ? styles.active : ''}`}
                            onClick={() => setContractorTab(contractorName, tab.key)}
                          >
                            <i className={tab.icon}></i>
                            {tab.label}
                          </button>
                        ))}
                      </div>

                      {/* Tab Content */}
                      <div className={styles.tabContent}>
                        {currentTab === 'overview' && (
                          <div className={styles.overviewTab}>
                            <div className={styles.overviewGrid}>
                              <div className={styles.overviewSection}>
                                <h4>Project Scope Summary</h4>
                                <p className={styles.scopeText}>
                                  {contractor.project_scope?.substring(0, 300)}
                                  {contractor.project_scope?.length > 300 && '...'}
                                </p>
                              </div>
                              
                              <div className={styles.overviewSection}>
                                <h4>Key Qualifications</h4>
                                <div className={styles.qualificationsList}>
                                  {contractor.qualifications?.length > 0 ? (
                                    contractor.qualifications.map((qual, idx) => (
                                      <div key={idx} className={styles.qualificationItem}>
                                        <i className="fas fa-certificate"></i>
                                        {qual}
                                      </div>
                                    ))
                                  ) : (
                                    <div className={styles.noData}>No qualifications specified</div>
                                  )}
                                </div>
                              </div>
                              
                              {allowancesData.length > 0 && (
                                <div className={styles.overviewSection}>
                                  <h4>Top Allowances</h4>
                                  <div className={styles.allowancesList}>
                                    {allowancesData.slice(0, 3).map((item, idx) => (
                                      <div key={idx} className={styles.allowanceItem}>
                                        <span className={styles.allowanceName}>{item.name}</span>
                                        <span className={styles.allowanceValue}>${item.value.toLocaleString()}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {currentTab === 'scope' && (
                          <div className={styles.scopeTab}>
                            <div className={styles.scopeGrid}>
                              <div className={styles.scopeSection}>
                                <h4>Work Categories</h4>
                                <div className={styles.categoriesList}>
                                  {Object.entries(contractor.categories || {}).map(([key, value]) => (
                                    <div key={key} className={styles.categoryItem}>
                                      <div className={styles.categoryHeader}>
                                        <i className="fas fa-wrench"></i>
                                        <span className={styles.categoryName}>
                                          {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                        </span>
                                      </div>
                                      <p className={styles.categoryDescription}>
                                        {value?.substring(0, 150)}
                                        {value?.length > 150 && '...'}
                                      </p>
                                    </div>
                                  ))}
                                </div>
                              </div>
                              
                              <div className={styles.scopeSection}>
                                <h4>Material Specifications</h4>
                                <div className={styles.specsList}>
                                  {Object.entries(contractor.specifications?.material_specification || {}).map(([key, value]) => (
                                    <div key={key} className={styles.specItem}>
                                      <span className={styles.specLabel}>{key.replace(/_/g, ' ')}:</span>
                                      <span className={styles.specValue}>{value}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        {currentTab === 'financial' && (
                          <div className={styles.financialTab}>
                            <div className={styles.financialGrid}>
                              <div className={styles.financialSection}>
                                <h4>Payment Structure</h4>
                                <div className={styles.paymentInfo}>
                                  <div className={styles.paymentItem}>
                                    <span className={styles.paymentLabel}>Total Cost:</span>
                                    <span className={styles.paymentValue}>${totalCost.toLocaleString()}</span>
                                  </div>
                                  <div className={styles.paymentItem}>
                                    <span className={styles.paymentLabel}>Payment Type:</span>
                                    <span className={styles.paymentValue}>
                                      {contractor.payment_terms?.pricing_structure?.replace(/_/g, ' ') || 'Not specified'}
                                    </span>
                                  </div>
                                  <div className={styles.paymentItem}>
                                    <span className={styles.paymentLabel}>Deposit:</span>
                                    <span className={styles.paymentValue}>
                                      ${parseCost(contractor.payment_terms?.deposit_required).toLocaleString()}
                                    </span>
                                  </div>
                                  {contractor.payment_terms?.breakdown_notes && (
                                    <div className={styles.paymentNotes}>
                                      <strong>Payment Schedule:</strong> {contractor.payment_terms.breakdown_notes}
                                    </div>
                                  )}
                                </div>
                              </div>
                              
                              {allowancesData.length > 0 && (
                                <div className={styles.financialSection}>
                                  <h4>Allowances Breakdown</h4>
                                  <div className={styles.allowancesChart}>
                                    <ResponsiveContainer width="100%" height={200}>
                                      <BarChart data={allowancesData}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                                        <XAxis 
                                          dataKey="name" 
                                          fontSize={10}
                                          angle={-45}
                                          textAnchor="end"
                                          height={60}
                                        />
                                        <YAxis tickFormatter={(value) => `$${(value/1000).toFixed(0)}k`} fontSize={10} />
                                        <Tooltip formatter={(value) => [`$${value.toLocaleString()}`, 'Amount']} />
                                        <Bar dataKey="value" fill={contractorColor} radius={[2, 2, 0, 0]} />
                                      </BarChart>
                                    </ResponsiveContainer>
                                  </div>
                                  <div className={styles.allowancesTotal}>
                                    <strong>Total Allowances: ${totalAllowances.toLocaleString()}</strong>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {currentTab === 'legal' && (
                          <div className={styles.legalTab}>
                            <div className={styles.legalGrid}>
                              <div className={styles.legalSection}>
                                <h4>License & Insurance</h4>
                                <div className={styles.legalInfo}>
                                  <div className={styles.legalItem}>
                                    <i className="fas fa-id-card"></i>
                                    <div>
                                      <div className={styles.legalLabel}>License Number</div>
                                      <div className={styles.legalValue}>
                                        {contractor.license_details?.license_number || 'Not specified'}
                                      </div>
                                    </div>
                                  </div>
                                  
                                  <div className={styles.legalItem}>
                                    <i className="fas fa-map-marked-alt"></i>
                                    <div>
                                      <div className={styles.legalLabel}>Jurisdiction</div>
                                      <div className={styles.legalValue}>
                                        {contractor.license_details?.jurisdiction || 'Not specified'}
                                      </div>
                                    </div>
                                  </div>
                                  
                                  <div className={styles.legalItem}>
                                    <i className="fas fa-shield-alt"></i>
                                    <div>
                                      <div className={styles.legalLabel}>Liability Coverage</div>
                                      <div className={styles.legalValue}>
                                        {contractor.insurance_details?.liability?.coverage || 'Not specified'}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              
                              <div className={styles.legalSection}>
                                <h4>Warranty & Terms</h4>
                                <div className={styles.warrantyInfo}>
                                  <div className={styles.warrantyItem}>
                                    <span className={styles.warrantyLabel}>Duration:</span>
                                    <span className={styles.warrantyValue}>
                                      {contractor.warranty_details?.workmanship?.warranty_duration || 'Not specified'}
                                    </span>
                                  </div>
                                  <div className={styles.warrantyItem}>
                                    <span className={styles.warrantyLabel}>Coverage:</span>
                                    <span className={styles.warrantyValue}>
                                      {contractor.warranty_details?.workmanship?.warranty_coverage || 'Not specified'}
                                    </span>
                                  </div>
                                  {contractor.warranty_details?.workmanship?.warranty_description && (
                                    <div className={styles.warrantyDescription}>
                                      {contractor.warranty_details.workmanship.warranty_description}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        {currentTab === 'logistics' && (
                          <div className={styles.logisticsTab}>
                            <div className={styles.logisticsGrid}>
                              <div className={styles.logisticsSection}>
                                <h4>Site Logistics</h4>
                                <div className={styles.logisticsInfo}>
                                  {Object.entries({
                                    'Working Hours': contractor.site_logistics?.working_hours,
                                    'Site Access': contractor.site_logistics?.site_access,
                                    'Material Storage': contractor.site_logistics?.material_storage,
                                    'Utility Access': contractor.site_logistics?.utility_access
                                  }).map(([label, value]) => (
                                    value && (
                                      <div key={label} className={styles.logisticsItem}>
                                        <span className={styles.logisticsLabel}>{label}:</span>
                                        <span className={styles.logisticsValue}>{value}</span>
                                      </div>
                                    )
                                  ))}
                                </div>
                              </div>
                              
                              <div className={styles.logisticsSection}>
                                <h4>Project Timeline</h4>
                                <div className={styles.timelineInfo}>
                                  <div className={styles.timelineItem}>
                                    <i className="fas fa-calendar-alt"></i>
                                    <div>
                                      <div className={styles.timelineLabel}>Estimated Duration</div>
                                      <div className={styles.timelineValue}>
                                        {contractor.timeline_details?.estimated_total_project_duration || 'Not specified'}
                                      </div>
                                    </div>
                                  </div>
                                  
                                  {contractor.timeline_details?.contingencies && (
                                    <div className={styles.timelineItem}>
                                      <i className="fas fa-exclamation-triangle"></i>
                                      <div>
                                        <div className={styles.timelineLabel}>Contingencies</div>
                                        <div className={styles.timelineValue}>
                                          {contractor.timeline_details.contingencies}
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                              
                              <div className={styles.logisticsSection}>
                                <h4>Cleanup & Responsibilities</h4>
                                <div className={styles.responsibilitiesInfo}>
                                  {contractor.cleanup_plan?.length > 0 && (
                                    <div className={styles.responsibilityGroup}>
                                      <strong>Cleanup Plan:</strong>
                                      <ul>
                                        {contractor.cleanup_plan.map((item, idx) => (
                                          <li key={idx}>{item}</li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}
                                  
                                  {contractor.client_responsibilities?.length > 0 && (
                                    <div className={styles.responsibilityGroup}>
                                      <strong>Client Responsibilities:</strong>
                                      <ul>
                                        {contractor.client_responsibilities.slice(0, 3).map((item, idx) => (
                                          <li key={idx}>{item}</li>
                                        ))}
                                        {contractor.client_responsibilities.length > 3 && (
                                          <li>... and {contractor.client_responsibilities.length - 3} more</li>
                                        )}
                                      </ul>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className={styles.lockedExpandedContent}>
                      <div className={styles.lockIcon}>
                        <i className="fas fa-lock"></i>
                      </div>
                      <h4>Detailed Contractor Profiles</h4>
                      <p>
                        Unlock comprehensive contractor analysis including detailed scope breakdown, 
                        financial terms, legal documentation, project timelines, and logistics planning.
                      </p>
                      <div className={styles.featureList}>
                        <div className={styles.featureItem}>
                          <i className="fas fa-check"></i>
                          Complete project scope & specifications
                        </div>
                        <div className={styles.featureItem}>
                          <i className="fas fa-check"></i>
                          Detailed financial breakdowns & allowances
                        </div>
                        <div className={styles.featureItem}>
                          <i className="fas fa-check"></i>
                          Insurance, licensing & legal terms
                        </div>
                        <div className={styles.featureItem}>
                          <i className="fas fa-check"></i>
                          Site logistics & timeline planning
                        </div>
                      </div>
                      <button className={styles.upgradeButton} onClick={onUpgradeClick}>
                        <i className="fas fa-crown"></i> 
                        Unlock Detailed Profiles
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>

      {/* Call to Action for Free Users */}
      {userPlan === 'free' && (
        <div className={styles.ctaSection}>
          <div className={styles.ctaContent}>
            <div className={styles.ctaIcon}>
              <i className="fas fa-users"></i>
            </div>
            <h3>Get Complete Contractor Insights</h3>
            <p>
              Unlock detailed contractor profiles with comprehensive analysis of project scope, 
              financial terms, legal documentation, and logistics planning to make the best decision for your project.
            </p>
            <button className={styles.ctaButton} onClick={onUpgradeClick}>
              <i className="fas fa-rocket"></i>
              Start with Essential Plan
            </button>
            <div className={styles.ctaBenefit}>
              <i className="fas fa-shield-check"></i>
              <span>Make informed decisions with complete contractor transparency</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContractorProfiles; 